"use client";

import { useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { toast } from "sonner";
import { tables } from "@/module_bindings";
import { useRowTransitions } from "@/hooks/spacetime/use-row-transitions";
import type { Organization } from "@/types/organization";
import type { OrgMemberRow } from "@/lib/stdb-map";

/**
 * Spec event #5: org_members.status invited -> active, shown to admin
 * viewers only (`enabled` is computed by the composer). `enabled: false`
 * nulls the resetKey, so non-admins never even baseline.
 */
export function InviteAcceptedToasts({
  organization,
  enabled,
}: {
  organization: Organization | null;
  enabled: boolean;
}) {
  const [rows, isReady] = useTable(tables.org_members);
  const orgRows = useMemo(
    () =>
      organization
        ? rows.filter(
            (row: OrgMemberRow) => row.organizationId === organization.id,
          )
        : [],
    [rows, organization],
  );

  useRowTransitions<OrgMemberRow>({
    rows: orgRows,
    isReady,
    getId: (row) => row.id,
    getState: (row) => row.status,
    resetKey: enabled ? (organization?.id ?? null) : null,
    onTransition: ({ prev, next, row }) => {
      if (prev !== "invited" || next !== "active") return;
      toast.success("Invitation Accepted", {
        id: `invite-accepted-${row.id}`,
        description: "A pending invitation to your organization was accepted.",
      });
    },
  });

  return null;
}
